import { Stack } from '@mantine/core';
import { BenefitStatus, OnboardingPeriodBenefitsDto } from '@zorro/clients';
import { Alert } from '@zorro/design-system/v2/Alert';
import { getToday, parseDateISO } from '@zorro/shared/formatters';
import { Text } from '@zorro/web/design-system';
import { useMonolithQuery, useRoles } from '@zorro/web/ui-utils';

import { FormFooter } from '../../FormFooter';
import { InsuredFormInputs, ReadonlyInsuredForm } from '../../InsuredForm';
import { useGlobalInsuredForm } from '../../InsuredForm/useGlobalInsuredForm';

type Props = {
  employerId: string;
  employeeId: string;
  isEmployeeMode?: boolean;
  withAgeAtPlanStart?: boolean;
};

export const GlobalPersonalDetailsForm = ({
  employerId,
  employeeId,
  isEmployeeMode = false,
  withAgeAtPlanStart = false,
}: Props) => {
  const { isZorroOperations } = useRoles();
  const isFinalizationMode = !isZorroOperations;
  const insuredForm = useGlobalInsuredForm({
    employeeId,
    isFinalizationMode,
  });
  const handleCancel = () => {
    insuredForm.employeeForm.reset();
    insuredForm.cancelSpouse();
    insuredForm.dependentsForm.reset();
  };

  const { isDirty: isEmployeeDirty } = insuredForm.employeeForm.formState;
  const { isDirty: isSpouseDirty } = insuredForm.spouseForm.formState;
  const { isDirty: isDependentsDirty } = insuredForm.dependentsForm.formState;

  const isAnyFieldTouched =
    isEmployeeDirty ||
    isSpouseDirty ||
    isDependentsDirty ||
    insuredForm.spouseStatus.spouseWasRemoved;

  const { data: onboardingPeriods } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindMany',
    params: [employeeId],
  });

  const { data: employeeBenefits } = useMonolithQuery({
    method: 'benefitsControllerGetAllEmployeeOnboardingPeriodBenefits',
    params: [employeeId],
  });
  const hasNonWaivedAndSubmittedBenefit = employeeBenefits?.some(
    (periodBenefits: OnboardingPeriodBenefitsDto) =>
      periodBenefits.majorMedicalBenefit &&
      periodBenefits.majorMedicalBenefit.status &&
      periodBenefits.majorMedicalBenefit.status !== BenefitStatus.IN_CART &&
      !periodBenefits.isWaived
  );

  const today = getToday();

  const hasEditableOP = onboardingPeriods?.some((op) => {
    if (!parseDateISO(op.effectiveFrom).isAfter(today)) return false;
    const periodBenefits = employeeBenefits?.find(
      (pb) => pb.onboardingPeriodId === op.id
    );
    return !periodBenefits?.majorMedicalBenefit?.submittedAt;
  });

  const isReadonly = !(isZorroOperations || hasEditableOP);

  return (
    <Stack maw={800} pb="5rem">
      {isReadonly ? (
        <ReadonlyInsuredForm
          isEmployeeMode={isEmployeeMode}
          insuredForm={insuredForm}
          withAgeAtPlanStart={withAgeAtPlanStart}
        />
      ) : (
        <>
          {hasNonWaivedAndSubmittedBenefit && (
            <Alert variant="intro">
              <Text size="sm">
                Changes made here will only apply to future election
                submissions. Existing enrollment records will remain unchanged
              </Text>
            </Alert>
          )}
          <InsuredFormInputs
            isFinalizationMode={isFinalizationMode}
            isEmployeeMode={isEmployeeMode}
            insuredForm={insuredForm}
            employerId={employerId}
            isGlobalMode
          />
        </>
      )}
      <FormFooter
        primaryLabel="Save"
        primaryButtonProps={{
          onClick: () => insuredForm.onSubmit(),
          disabled: !isAnyFieldTouched,
        }}
        secondaryLabel={isAnyFieldTouched ? 'Cancel' : undefined}
        secondaryButtonProps={{ onClick: handleCancel }}
      />
    </Stack>
  );
};
